/**
 * Shared shooting-percentage color helper for SmarterHoops (build 210).
 *
 * Thresholds are tuned for **youth basketball (14U and below)**, not
 * NBA-level shooting. The NBA-tier numbers we shipped originally made
 * every youth player render red on every column, which misrepresented
 * actual performance relative to peers. Free-throw threshold is held
 * higher than field goals because FTs are uncontested — players should
 * be measured against a stricter bar at the line.
 *
 * Tier rules:
 *   FG%  : green ≥ 45,  yellow ≥ 35,  red < 35
 *   3PT% : green ≥ 33,  yellow ≥ 25,  red < 25
 *   FT%  : green ≥ 70,  yellow ≥ 55,  red < 55
 *   eFG% : green ≥ 47,  yellow ≥ 38,  red < 38
 *   TS%  : green ≥ 50,  yellow ≥ 40,  red < 40
 *
 * Two consumer flavours:
 *   • `getPctColor(pct, attempts, type)`     → Tailwind class string
 *     (used by GameSummary box-score cells styled with `className`).
 *   • `getPctColorHex(pct, attempts, type)`  → hex color string
 *     (used by PlayerStats / PlayerProfile SplitTile components that
 *      consume an inline-style `color` prop).
 * Both share a single `_tier()` implementation so the thresholds and
 * neutral-state rules can never drift between surfaces.
 *
 * Call sites can either:
 *   1. Pass `attempts` (preferred) — the helper returns neutral for
 *      `attempts === 0` regardless of what `pct` is. This matches the
 *      "0 attempts shows '-'" UI rule in `formatPct`.
 *   2. Pass `attempts` as `undefined` — the helper falls back to
 *      treating `pct == null` as neutral. Useful for season-aggregate
 *      surfaces that have already collapsed 0-attempts into null via
 *      a `pct(n, d)` helper before reaching here.
 */

const TIER = Object.freeze({ GOOD: 'good', OK: 'ok', POOR: 'poor', NEUTRAL: 'neutral' });

const THRESHOLDS = Object.freeze({
  fg:  { good: 45, ok: 35 },
  '3pt': { good: 33, ok: 25 },
  ft:  { good: 70, ok: 55 },
  efg: { good: 47, ok: 38 },
  ts:  { good: 50, ok: 40 },
});

const CLASS_MAP = Object.freeze({
  good: 'text-green-600',
  ok:   'text-yellow-600',
  poor: 'text-red-600',
  neutral: 'text-slate-600',
});

const HEX_MAP = Object.freeze({
  good: '#16a34a',   // tailwind green-600
  ok:   '#ca8a04',   // tailwind yellow-600
  poor: '#dc2626',   // tailwind red-600
  neutral: '#475569', // tailwind slate-600
});

function _tier(pct, attempts, type) {
  // Neutral first — captures both "0 attempts passed" and "no value at all".
  if (attempts === 0) return TIER.NEUTRAL;
  if (pct === null || pct === undefined || Number.isNaN(pct)) return TIER.NEUTRAL;
  const t = THRESHOLDS[type];
  if (!t) return TIER.NEUTRAL; // unknown type → never colour
  if (pct >= t.good) return TIER.GOOD;
  if (pct >= t.ok)   return TIER.OK;
  return TIER.POOR;
}

/**
 * Tailwind class variant (GameSummary box-score cells).
 * @param {number|null|undefined} pct  Percentage as 0-100 (NOT a 0-1 fraction).
 * @param {number|null|undefined} attempts  Shot attempts. Pass `undefined` if
 *   the caller has already collapsed 0-attempts to `null` upstream.
 * @param {'fg'|'3pt'|'ft'|'efg'|'ts'} type
 * @returns {string} Tailwind text-color class.
 */
export function getPctColor(pct, attempts, type) {
  return CLASS_MAP[_tier(pct, attempts, type)];
}

/**
 * Hex variant (SplitTile inline `color` style).
 * Returns `null` when neutral so SplitTile can fall back to its own
 * default ink colour instead of forcing slate-grey on uncoloured tiles.
 * @returns {string|null}
 */
export function getPctColorHex(pct, attempts, type) {
  const tier = _tier(pct, attempts, type);
  if (tier === TIER.NEUTRAL) return null;
  return HEX_MAP[tier];
}
